/**
 * Message-list display preference — snippet preview lines per row (1–3).
 *
 * Now a HOLDER-WIDE server setting (see `useEmailUserSettings`): it follows the
 * user across accounts and devices instead of living in per-device localStorage.
 * This hook is a thin adapter that keeps the old `[prefs, update]` shape so
 * consumers are unchanged.
 */

import { useEmailUserSettings, useSetEmailUserSettings } from './useEmailUserSettings';

export type PreviewLines = 1 | 2 | 3;

export interface EmailListPrefs {
  /** Snippet preview lines shown per message row. */
  previewLines: PreviewLines;
}

export const PREVIEW_LINE_OPTIONS: PreviewLines[] = [1, 2, 3];

export const DEFAULT_EMAIL_LIST_PREFS: EmailListPrefs = { previewLines: 1 };

/** Clamp an arbitrary value back into the 1|2|3 union. */
export function coercePreviewLines(raw: unknown): PreviewLines {
  const n = Number(raw);
  return n === 2 ? 2 : n === 3 ? 3 : 1;
}

/**
 * Read + update the message-list preview length. Backed by the holder-wide
 * settings store, so a change applies across every account and device.
 */
export function useEmailListPrefs(): [EmailListPrefs, (next: Partial<EmailListPrefs>) => void] {
  const settings = useEmailUserSettings();
  const setAll = useSetEmailUserSettings();
  const update = (next: Partial<EmailListPrefs>) => {
    const previewLines = coercePreviewLines(next.previewLines ?? settings.previewLines);
    setAll.mutate({ ...settings, previewLines });
  };
  return [{ previewLines: settings.previewLines }, update];
}
